'use client';
import React from 'react';
import { safeSanitize } from '@/core/utils/sanitize';
import Link from '@/core/i18n/navigation';
import { BlockProps } from './types';
import { useLocalizeHtml } from '@/core/i18n/navigation';
import { cn } from '@/core/utils/cn';

const LEVEL_CLASSES: Record<string, string> = {
  h1: 'text-4xl md:text-5xl font-extrabold tracking-tight',
  h2: 'text-3xl md:text-4xl font-bold tracking-tight',
  h3: 'text-2xl md:text-3xl font-bold',
  h4: 'text-xl md:text-2xl font-semibold',
  h5: 'text-lg font-semibold',
  h6: 'text-base font-semibold',
};

// Admin-only mirror of LEVEL_CLASSES' mobile-vs-md: split, keyed by simulated viewport
// instead of the (inert, in the scaled admin canvas) md: breakpoint — see the note in the
// component below for why the bare classes above never react to the viewport toggle in admin.
const LEVEL_CLASSES_MOBILE: Record<string, string> = {
  h1: 'text-4xl font-extrabold tracking-tight',
  h2: 'text-3xl font-bold tracking-tight',
  h3: 'text-2xl font-bold',
  h4: 'text-xl font-semibold',
  h5: 'text-lg font-semibold',
  h6: 'text-base font-semibold',
};
const LEVEL_CLASSES_DESKTOP: Record<string, string> = {
  h1: 'text-5xl font-extrabold tracking-tight',
  h2: 'text-4xl font-bold tracking-tight',
  h3: 'text-3xl font-bold',
  h4: 'text-2xl font-semibold',
  h5: 'text-lg font-semibold',
  h6: 'text-base font-semibold',
};

export function HeaderBlock({ block, currentStyles, innerElementStyles, resolveTags, isAdmin, viewport }: BlockProps) {
  const localizeHtml = useLocalizeHtml();
  const level = block.settings?.level || 'h2';
  const HeaderTag = level as any;
  // The admin canvas simulates viewport by scaling a real-width div — md: below keys off the
  // real browser window, not that scaled width, so it never reacts to the viewport toggle
  // here. Canvas tablet width (768) already matches md:'s own breakpoint, so only mobile
  // (375) needs the smaller class; tablet and desktop share the same md: class regardless.
  const defaultLevelClass = isAdmin
    ? (viewport === 'mobile' ? LEVEL_CLASSES_MOBILE : LEVEL_CLASSES_DESKTOP)[level] || LEVEL_CLASSES_DESKTOP.h2
    : LEVEL_CLASSES[level] || LEVEL_CLASSES.h2;

  // Only the hover colour needs animating. `transition-all` on the live storefront also animated
  // any font-size/line-height change (fonts, viewport correction) over several frames, and every
  // frame of a moving heading counts as a separate layout shift. The editor keeps the smooth
  // preview when switching its simulated viewport.
  const transitionClass = isAdmin ? 'transition-all' : 'transition-colors';

  const heading = block.settings?.useLink ? (
    <Link href={block.settings?.href || '#'}>
      <HeaderTag
        className={cn("relative leading-tight hover:text-[var(--hover-color)] cursor-pointer", transitionClass, defaultLevelClass)}
        style={innerElementStyles as any}
        dangerouslySetInnerHTML={{ __html: localizeHtml(safeSanitize(resolveTags(block.content))) }}
      />
    </Link>
  ) : (
    <HeaderTag
      className={cn("relative leading-tight hover:text-[var(--hover-color)]", transitionClass, defaultLevelClass)}
      style={innerElementStyles as any}
      dangerouslySetInnerHTML={{ __html: localizeHtml(safeSanitize(resolveTags(block.content))) }}
    />
  );

  // A large faint word behind the heading ("Clients" behind "Klienci") — decoration only:
  // aria-hidden so neither search engines nor screen readers read it as content, clipped
  // horizontally so it can never make the page scroll sideways.
  const watermark = typeof block.settings?.watermark === 'string' ? block.settings.watermark.trim() : '';
  if (!watermark) return heading;
  const hideOnMobile = block.settings?.watermarkHideMobile !== false;
  const opacity = Math.min(100, Math.max(0, Number(block.settings?.watermarkOpacity ?? 6))) / 100;
  const align = innerElementStyles?.textAlign === 'center' ? 'center' : innerElementStyles?.textAlign === 'right' ? 'right' : 'left';
  return (
    <div className="relative isolate" style={{ overflowX: 'clip' }}>
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none select-none absolute top-1/2 -z-10 whitespace-nowrap font-bold leading-none',
          align === 'center' ? 'left-1/2' : align === 'right' ? 'right-0' : 'left-0',
          hideOnMobile && (isAdmin ? (viewport === 'mobile' && 'hidden') : 'max-sm:hidden'),
        )}
        style={{
          fontSize: `${Number(currentStyles?.watermarkSize ?? 160)}px`,
          color: block.settings?.watermarkColor || '#000000',
          opacity,
          transform: `translate(${align === 'center' ? '-50%' : '0'}, -50%) translate(${Number(currentStyles?.watermarkOffsetX ?? 0)}px, ${Number(currentStyles?.watermarkOffsetY ?? 0)}px)`,
          fontFamily: block.settings?.watermarkFontFamily || undefined,
        }}
      >
        {watermark}
      </span>
      {heading}
    </div>
  );
}
